import type { ReactNode } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import type { LoyaltyProgram } from '@voll-intelligence/domain';
import type { ChatUiProductCheckout } from '@voll-intelligence/genui-contract';
import { buttonVariants } from '@voll-intelligence/ui-kit/ui/button';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { cn } from '@voll-intelligence/ui-kit/utils';
import { useGenUiI18n, type GenUiKey } from '../../../shared/ui/i18n.js';
import type { UseOneClickCheckout } from '../core/use_one_click_checkout.js';
import type { LoyaltyPlans } from '../port.js';
import { loyaltyEntries, maskLoyaltyNumber, type LoyaltyEntry } from './loyalty_entries.js';

/** Quem está logado viaja sozinho: só então o perfil lido é o do viajante. */
export function travelsAlone({ travelers, requesterId }: ChatUiProductCheckout): boolean {
  const [only, ...rest] = travelers ?? [];
  return Boolean(requesterId) && rest.length === 0 && only?.id === requesterId;
}

/** O card ainda pede (conferência aberta): fora disso ele é registro. */
export function oneClickLive({ stage }: UseOneClickCheckout): boolean {
  return stage.kind === 'loading' || stage.kind === 'error' || stage.kind === 'ready';
}

/** Metades no card largo, uma coluna no estreito (a grade do painel). */
const GRID = 'grid grid-cols-2 gap-3 @max-[theme(--breakpoint-sm)]/checkout:grid-cols-1';
const NAME = 'm-0 truncate text-[13px] leading-[18px] font-medium text-foreground';

function Identified({
  entry,
  mark,
  appliedKey,
}: {
  readonly entry: LoyaltyEntry & { number: string };
  readonly mark: ReactNode;
  readonly appliedKey: GenUiKey;
}) {
  const { t } = useGenUiI18n();
  return (
    <div className="flex min-w-0 items-center gap-2">
      {mark}
      <div className="min-w-0">
        <p className={NAME}>
          {entry.program.name}{' '}
          <span className="tabular-nums">{maskLoyaltyNumber(entry.number)}</span>
        </p>
        <p className="m-0 truncate text-xs font-medium text-muted-foreground">{t(appliedKey)}</p>
      </div>
    </div>
  );
}

/** «Adicionar programa de fidelidade» — um botão só (o + e o texto), que leva à
 *  tela do perfil já no programa. O nome do programa vai no rótulo acessível: com
 *  duas companhias sem programa, as duas linhas teriam o mesmo texto. */
function AddEntry({
  entry,
  onAdd,
}: {
  readonly entry: LoyaltyEntry;
  readonly onAdd: (programId: string) => void;
}) {
  const { t } = useGenUiI18n();
  return (
    <button
      type="button"
      aria-label={t('productCheckout.loyalty.addAria', { program: entry.program.name })}
      className="group flex min-w-0 items-center gap-2 self-center rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      onClick={() => {
        onAdd(entry.program.id);
      }}
    >
      <span
        aria-hidden
        className={cn(buttonVariants({ variant: 'outline', size: 'icon-xs' }), 'shrink-0')}
      >
        <PlusIcon />
      </span>
      <span className={NAME}>{t('productCheckout.loyalty.add')}</span>
    </button>
  );
}

/** A marca de um programa de hotel ou de locadora: a logo curada que o host dá
 *  (`LoyaltyPort.logoOf`) num quadrado branco com borda, como na aba Fidelidade;
 *  sem ela, o ícone do tipo num quadrado neutro. Decorativa: o nome está ao lado. */
export function CatalogProgramMark({
  logo,
  icon,
}: {
  readonly logo: string | null;
  readonly icon: ReactNode;
}) {
  return logo ? (
    <img
      src={logo}
      alt=""
      className="size-7 shrink-0 rounded-md bg-white object-contain p-0.5 ring-1 ring-border"
    />
  ) : (
    <span
      aria-hidden
      className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground [&_svg]:size-4"
    >
      {icon}
    </span>
  );
}

/** O esqueleto do «Carregando…»: logo e texto por programa. */
function LoyaltySkeleton({ count }: { readonly count: number }) {
  return (
    <div className={GRID} aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex items-center gap-2">
          <Skeleton className="size-8 shrink-0" />
          <Skeleton className="h-8 w-45 max-w-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * A linha de fidelidade do card 1click (#5340 aéreo, #6159 carro e hotel): uma
 * entrada por programa do card — «<programa> •••• 1234 · <aplicado>» quando o
 * perfil o tem, «Adicionar programa de fidelidade» quando não.
 *
 * `record`: o card já pediu (ou é registro do histórico) — a linha fica só com
 * os programas identificados, e some sem nenhum; nunca «Adicionar» depois do
 * pedido. Leitura com erro some (não dá para afirmar que falta o programa).
 */
export function CheckoutLoyaltyLine({
  programs,
  usePlans,
  record,
  renderMark,
  appliedKey,
  onAdd,
}: {
  readonly programs: readonly LoyaltyProgram[];
  /** O hook de leitura do port; o port é do host e não troca durante a vida do
   *  card, então chamá-lo daqui segue as regras de hooks. */
  readonly usePlans: () => LoyaltyPlans;
  readonly record: boolean;
  readonly renderMark: (program: LoyaltyProgram) => ReactNode;
  readonly appliedKey: GenUiKey;
  readonly onAdd: (programId: string) => void;
}) {
  const { t } = useGenUiI18n();
  // eslint-disable-next-line react/hooks -- the host injects a stable hook through the port; moving the port to a component or store is #6816
  const plans = usePlans();
  if (programs.length === 0 || plans.status === 'error') return null;
  if (plans.status === 'loading') {
    return record ? null : <LoyaltySkeleton count={programs.length} />;
  }
  const entries = loyaltyEntries(programs, plans.plans);
  const shown = record ? entries.filter((entry) => entry.number !== null) : entries;
  if (shown.length === 0) return null;
  return (
    <section className={GRID} aria-label={t('productCheckout.loyalty.aria')}>
      {shown.map((entry) =>
        entry.number !== null ? (
          <Identified
            key={entry.program.id}
            entry={{ ...entry, number: entry.number }}
            mark={renderMark(entry.program)}
            appliedKey={appliedKey}
          />
        ) : (
          <AddEntry key={entry.program.id} entry={entry} onAdd={onAdd} />
        ),
      )}
    </section>
  );
}
